import { useEffect, useRef } from 'react';
import { useChatStore } from '../store/chat-store';
import type { ChatClient } from '../socket/socket-client';

interface Props {
  client: ChatClient | null;
}

/**
 * Celebratory popup shown when a user becomes VIP. Triggered by the
 * `notif:vip-activated` WS event (live or replayed on reconnect).
 *
 * Acks back to the server via `notif:seen` if a notificationId is present
 * (i.e. a persisted in-app notification rather than a transient live push).
 */
export function ActivationModal({ client }: Props) {
  const activation = useChatStore(s => s.activation);
  const dismiss = useChatStore(s => s.dismissActivation);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!activation) return;
    buttonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        e.preventDefault();
        handleDismiss();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activation]);

  if (!activation) return null;

  function handleDismiss() {
    if (activation?.notificationId && client) {
      client.ackNotificationSeen(activation.notificationId);
    }
    dismiss();
  }

  const triggerLabel =
    activation.trigger === 'manual-grant'
      ? 'An admin just unlocked VIP for your account.'
      : 'You just hit the VIP deposit threshold.';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="activation-title"
      className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
    >
      <div className="mx-4 w-full max-w-sm rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden">
        <div className="vip-header-bg bg-gradient-to-br from-amber-400 via-pink-500 to-violet-600 p-6 text-center text-white">
          <div className="text-5xl mb-2" aria-hidden="true">🎉</div>
          <h2 id="activation-title" className="text-xl font-bold tracking-tight">
            Welcome to VIP Chat!
          </h2>
        </div>
        <div className="p-6 text-center space-y-4">
          <p className="text-sm text-gray-700">{triggerLabel}</p>
          <p className="text-xs text-gray-500">
            You now have full access to the live chat room.
          </p>
          <button
            ref={buttonRef}
            type="button"
            onClick={handleDismiss}
            className="inline-flex w-full items-center justify-center rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 transition"
          >
            Start chatting
          </button>
        </div>
      </div>
    </div>
  );
}
